// components/ApplicantGallery.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { galleryService } from '../services/galleryService';
import ApplicantCard from './ApplicantCard';
import './ApplicantGallery.css';

// Helper function to get CompanyID directly from token
const getCompanyIdFromToken = () => {
    try {
        const token = localStorage.getItem('token');
        if (!token) {
            console.warn('⚠️ No token found in localStorage');
            return null;
        }
        
        const payloadBase64 = token.split('.')[1];
        const decodedPayload = JSON.parse(atob(payloadBase64.replace(/-/g, '+').replace(/_/g, '/')));
        
        const companyId = decodedPayload.companyId || 
                         decodedPayload.CompanyID || 
                         decodedPayload.companyID ||
                         decodedPayload.CompanyId ||
                         decodedPayload["CompanyID"] ||
                         null;
        
        /*if (!companyId) {
            console.warn('⚠️ No CompanyID found in token. Available claims:', Object.keys(decodedPayload));
        } else {
            console.log('✅ CompanyID found in token:', companyId);
        }*/
        
        return companyId;
    } catch (error) {
        console.error('❌ Error decoding token for CompanyID:', error);
        return null;
    }
};

const ApplicantGallery = () => {
    // State hooks
    const [companyId, setCompanyId] = useState(null);
    const [items, setItems] = useState([]);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [lastRowNumber, setLastRowNumber] = useState(null);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [isVideoLoading, setIsVideoLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    
    // ✅ Filter & Sort State
    const [sortBy, setSortBy] = useState('name');
    const [sortDirection, setSortDirection] = useState('desc');
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        nationality: '',
        religion: '',
        maritalStatus: '',
        minAge: '',
        maxAge: '',
        minSalary: '',
        maxSalary: '',
        minExperience: '',
        maxExperience: '',
        skills: [],
        hasVideo: null,
        hasPhoto: null
    });
    
    // ✅ Debounced filter values for API calls
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [debouncedFilters, setDebouncedFilters] = useState(filters);
    
    const limit = 20;

    // Responsive masonry layout with CSS columns
    const getColumnCount = () => {
        if (window.innerWidth < 500) return 1;
        if (window.innerWidth < 700) return 2;
        if (window.innerWidth < 1100) return 3;
        return 4;
    };

    const [columnCount, setColumnCount] = useState(getColumnCount());

    // Refs
    const observer = useRef(null);
    const fetchApplicantsRef = useRef(null);
    const isFetchingRef = useRef(false);
    const searchTimeoutRef = useRef(null);
    const filterTimeoutRef = useRef(null);

    // ✅ Debounce search term
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500); // 500ms delay
        
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm]);

    // ✅ Debounce filters (except skills and boolean filters which are instant)
    useEffect(() => {
        if (filterTimeoutRef.current) {
            clearTimeout(filterTimeoutRef.current);
        }
        
        // Only debounce text and range inputs
        const debounceKeys = ['nationality', 'religion', 'maritalStatus', 'minAge', 'maxAge', 'minSalary', 'maxSalary', 'minExperience', 'maxExperience'];
        const shouldDebounce = debounceKeys.some(key => {
            if (key.includes('min') || key.includes('max')) {
                // For range inputs, only debounce if both min and max are filled or one is filled
                return filters[key] !== '';
            }
            return filters[key] !== '';
        });
        
        if (shouldDebounce) {
            filterTimeoutRef.current = setTimeout(() => {
                setDebouncedFilters(filters);
            }, 400); // 400ms delay for filters
        } else {
            // For instant filters (skills, boolean), update immediately
            setDebouncedFilters(filters);
        }
        
        return () => {
            if (filterTimeoutRef.current) {
                clearTimeout(filterTimeoutRef.current);
            }
        };
    }, [filters]);

    // ✅ Build filter options for API
    const buildFilterOptions = useCallback((filterData) => {
        const filterOptions = {};
        
        if (filterData.nationality) filterOptions.nationality = filterData.nationality;
        if (filterData.religion) filterOptions.religion = filterData.religion;
        if (filterData.maritalStatus) filterOptions.maritalStatus = filterData.maritalStatus;
        
        // ✅ Only include range values if both min and max are valid
        if (filterData.minAge && filterData.maxAge) {
            const minAge = parseInt(filterData.minAge);
            const maxAge = parseInt(filterData.maxAge);
            if (!isNaN(minAge) && !isNaN(maxAge) && minAge <= maxAge) {
                filterOptions.minAge = minAge;
                filterOptions.maxAge = maxAge;
            }
        } else if (filterData.minAge) {
            const minAge = parseInt(filterData.minAge);
            if (!isNaN(minAge)) filterOptions.minAge = minAge;
        } else if (filterData.maxAge) {
            const maxAge = parseInt(filterData.maxAge);
            if (!isNaN(maxAge)) filterOptions.maxAge = maxAge;
        }
        
        if (filterData.minSalary && filterData.maxSalary) {
            const minSalary = parseFloat(filterData.minSalary);
            const maxSalary = parseFloat(filterData.maxSalary);
            if (!isNaN(minSalary) && !isNaN(maxSalary) && minSalary <= maxSalary) {
                filterOptions.minSalary = minSalary;
                filterOptions.maxSalary = maxSalary;
            }
        } else if (filterData.minSalary) {
            const minSalary = parseFloat(filterData.minSalary);
            if (!isNaN(minSalary)) filterOptions.minSalary = minSalary;
        } else if (filterData.maxSalary) {
            const maxSalary = parseFloat(filterData.maxSalary);
            if (!isNaN(maxSalary)) filterOptions.maxSalary = maxSalary;
        }
        
        if (filterData.minExperience && filterData.maxExperience) {
            const minExp = parseInt(filterData.minExperience);
            const maxExp = parseInt(filterData.maxExperience);
            if (!isNaN(minExp) && !isNaN(maxExp) && minExp <= maxExp) {
                filterOptions.minExperience = minExp;
                filterOptions.maxExperience = maxExp;
            }
        } else if (filterData.minExperience) {
            const minExp = parseInt(filterData.minExperience);
            if (!isNaN(minExp)) filterOptions.minExperience = minExp;
        } else if (filterData.maxExperience) {
            const maxExp = parseInt(filterData.maxExperience);
            if (!isNaN(maxExp)) filterOptions.maxExperience = maxExp;
        }
        
        if (filterData.skills && filterData.skills.length > 0) filterOptions.skills = filterData.skills;
        if (filterData.hasVideo !== null) filterOptions.hasVideo = filterData.hasVideo;
        if (filterData.hasPhoto !== null) filterOptions.hasPhoto = filterData.hasPhoto;
        
        return filterOptions;
    }, []);

    // ✅ Fetch applicants function
    const fetchApplicants = useCallback(async (forceReset = false) => {
        if (!companyId || companyId === 'undefined' || companyId === 'null') {
            //console.error('❌ Invalid companyId:', companyId);
            setError('Company ID not found. Please login again.');
            return;
        }
        
        if (isFetchingRef.current) {
            //console.log('⏳ Fetch already in progress, skipping...');
            return;
        }
        
        if (loading) {
            //console.log('⏳ Loading in progress, skipping...');
            return;
        }
        
        if (!hasMore && !forceReset) {
            //console.log('🏁 No more items to load');
            return;
        }
        
        const currentLastRowNumber = forceReset ? null : lastRowNumber;
        //console.log('📡 Fetching with lastRowNumber:', currentLastRowNumber);
        
        isFetchingRef.current = true;
        setLoading(true);
        setError(null);

        try {
            const filterOptions = buildFilterOptions(debouncedFilters);
            
            const result = await galleryService.getApplicantsInfinite(
                companyId,
                limit,
                currentLastRowNumber,
                debouncedSearchTerm || undefined,
                null,
                sortBy,
                sortDirection,
                Object.keys(filterOptions).length > 0 ? filterOptions : null
            );
            
            /*console.log('📡 API Response:', {
                itemsCount: result.items.length,
                hasMore: result.hasMore,
                nextRowNumber: result.nextRowNumber,
                totalCount: result.totalCount
            });*/
            
            if (result.items.length === 0) {
                setHasMore(false);
                setLoading(false);
                isFetchingRef.current = false;
                return;
            }
            
            if (forceReset) {
                setItems(result.items);
            } else {
                setItems(prevItems => {
                    const existingIds = new Set(prevItems.map(item => item.id));
                    const newItems = result.items.filter(item => !existingIds.has(item.id));
                    /*console.log('📊 Items update:', {
                        previousCount: prevItems.length,
                        newItemsCount: newItems.length,
                        totalAfterUpdate: prevItems.length + newItems.length
                    });*/
                    return [...prevItems, ...newItems];
                });
            }

            setHasMore(result.hasMore);
            if (result.nextRowNumber !== null && result.nextRowNumber !== undefined) {
                setLastRowNumber(result.nextRowNumber);
            }
            
            if (currentLastRowNumber === null && result.totalCount > 0) {
                setTotalCount(result.totalCount);
                //console.log('📊 Total applicants set:', result.totalCount);
            }
            
        } catch (err) {
            console.error('❌ Error fetching applicants:', err);
            setError(err.message);
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    }, [companyId, lastRowNumber, loading, hasMore, debouncedSearchTerm, debouncedFilters, sortBy, sortDirection, buildFilterOptions]);

    // Store fetchApplicants in ref
    useEffect(() => {
        fetchApplicantsRef.current = fetchApplicants;
    }, [fetchApplicants]);

    // Initialize companyId from token
    useEffect(() => {
        const id = getCompanyIdFromToken();
        setCompanyId(id);
        //console.log('🔍 Company ID set to:', id);
    }, []);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setColumnCount(getColumnCount());
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch total count only once on mount
    useEffect(() => {
        const fetchTotalCount = async () => {
            if (companyId && companyId !== 'undefined' && companyId !== 'null') {
                try {
                    const count = await galleryService.getTotalCount(companyId);
                    setTotalCount(count);
                    //console.log('📊 Total available applicants:', count);
                } catch (error) {
                    //console.error('❌ Error fetching total count:', error);
                }
            }
        };
        fetchTotalCount();
    }, [companyId]);

    // Pre-warm cache on mount
    useEffect(() => {
        if (companyId && companyId !== 'undefined' && companyId !== 'null') {
            galleryService.preWarmCache(companyId);
        }
    }, [companyId]);

    // Initial fetch when companyId is set
    useEffect(() => {
        if (companyId && companyId !== 'undefined' && companyId !== 'null' && items.length === 0) {
            //console.log('🚀 Initial fetch...');
            fetchApplicantsRef.current?.(true);
        }
    }, [companyId]);

    // ✅ Fetch when debounced search, filters, or sorting changes
    useEffect(() => {
        if (companyId && companyId !== 'undefined' && companyId !== 'null') {
            // Reset everything on filter/sort change
            setItems([]);
            setLastRowNumber(null);
            setHasMore(true);
            isFetchingRef.current = false;
            setTimeout(() => {
                fetchApplicantsRef.current?.(true);
            }, 100);
        }
    }, [debouncedSearchTerm, sortBy, sortDirection, debouncedFilters]);

    // Cleanup observer on unmount
    useEffect(() => {
        return () => {
            if (observer.current) {
                observer.current.disconnect();
                observer.current = null;
            }
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            if (filterTimeoutRef.current) {
                clearTimeout(filterTimeoutRef.current);
            }
        };
    }, []);

    // Last item ref callback
    const lastItemRef = useCallback((node) => {
        if (observer.current) {
            observer.current.disconnect();
            observer.current = null;
        }

        if (!node) return;
        if (loading) return;
        if (!hasMore) return;
        if (isFetchingRef.current) return;

        observer.current = new IntersectionObserver(
            (entries) => {
                const firstEntry = entries[0];
                if (firstEntry.isIntersecting && hasMore && !loading && !isFetchingRef.current) {
                    //console.log('👀 Last item visible, fetching more...');
                    fetchApplicantsRef.current?.(false);
                }
            },
            {
                root: null,
                rootMargin: '200px',
                threshold: 0.01
            }
        );

        observer.current.observe(node);
    }, [loading, hasMore]);

    // Event handlers
    const handleVideoClick = (videoUrl) => {
        setIsVideoLoading(true);
        setSelectedVideo(videoUrl);
        setTimeout(() => setIsVideoLoading(false), 500);
    };

    const handleCloseVideo = () => {
        setSelectedVideo(null);
        setIsVideoLoading(false);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        // Search is handled by debounce
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setDebouncedSearchTerm('');
    };

    // ✅ Filter handlers - these update the local state immediately, but debounced for API
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleSkillToggle = (skill) => {
        setFilters(prev => {
            const skills = prev.skills.includes(skill)
                ? prev.skills.filter(s => s !== skill)
                : [...prev.skills, skill];
            return { ...prev, skills };
        });
    };

    const handleBooleanFilter = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: prev[key] === value ? null : value }));
    };

    const clearFilters = () => {
        setFilters({
            nationality: '',
            religion: '',
            maritalStatus: '',
            minAge: '',
            maxAge: '',
            minSalary: '',
            maxSalary: '',
            minExperience: '',
            maxExperience: '',
            skills: [],
            hasVideo: null,
            hasPhoto: null
        });
        setDebouncedFilters({
            nationality: '',
            religion: '',
            maritalStatus: '',
            minAge: '',
            maxAge: '',
            minSalary: '',
            maxSalary: '',
            minExperience: '',
            maxExperience: '',
            skills: [],
            hasVideo: null,
            hasPhoto: null
        });
        setSortBy('name');
        setSortDirection('desc');
    };

    // Check token status
    const token = localStorage.getItem('token');
    const isLoggedIn = !!token;

    // Available filter options
    const nationalities = ['Ethiopia', 'Philippines', 'India', 'Sri Lanka', 'Nepal', 'Egypt', 'Kenya', 'Uganda'];
    const religions = ['Christian', 'Muslim', 'Hindu', 'Buddhist', 'Other'];
    const maritalStatuses = ['Single', 'Married', 'Divorced', 'Widowed'];
    const skillOptions = ['Ironing', 'Sewing', 'BabySitting', 'Cleaning', 'Washing', 'ChildCare', 'ArabicCooking'];

    // Conditional returns
    if (!isLoggedIn) {
        return (
            <div className="gallery-error">
                <p>Please login to view applicants</p>
                <button onClick={() => window.location.href = '/login'}>
                    Go to Login
                </button>
            </div>
        );
    }

    if (!companyId || companyId === 'undefined' || companyId === 'null') {
        return (
            <div className="gallery-error">
                <p>⚠️ Company ID not found in your profile.</p>
                <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                    Please contact support or login again.
                </p>
                <button onClick={() => {
                    localStorage.removeItem('token');
                    window.location.href = '/login';
                }}>
                    Login Again
                </button>
                <button 
                    onClick={() => {
                        const token = localStorage.getItem('token');
                        if (token) {
                            try {
                                const payload = JSON.parse(atob(token.split('.')[1]));
                                //console.log('📋 Full token payload:', payload);
                                //console.log('📋 All claims:', Object.keys(payload));
                                alert('Check console for token details');
                            } catch (e) {
                                console.error('Error decoding token:', e);
                            }
                        }
                    }}
                    style={{ marginLeft: '10px', background: '#666' }}
                >
                    Debug Token
                </button>
            </div>
        );
    }

    if (error) {
        return (
            <div className="gallery-error">
                <p>Error loading applicants: {error}</p>
                <button onClick={() => window.location.reload()}>Retry</button>
            </div>
        );
    }

    // Split items into columns for masonry layout
    const getColumns = () => {
        const columns = Array.from({ length: columnCount }, () => []);
        items.forEach((item, index) => {
            columns[index % columnCount].push(item);
        });
        return columns;
    };

    const displayedCount = items.length;
    const totalAvailable = totalCount > 0 ? totalCount : '...';

    const hasActiveFilters = Object.keys(filters).some(key => {
        if (key === 'skills') return filters.skills.length > 0;
        if (key === 'hasVideo' || key === 'hasPhoto') return filters[key] !== null;
        return filters[key] !== '' && filters[key] !== null;
    });

    return (
        <div className="gallery-container">
            {/* Header with Title and Available Count */}
            <div className="gallery-header">
                <div className="gallery-title-section">
                    <h2>Applicants Gallery</h2>
                    {totalCount > 0 && (
                        <span className="available-count">
                            {totalCount.toLocaleString()} Available {totalCount === 1 ? 'Applicant' : 'Applicants'}
                        </span>
                    )}
                </div>
                
                <div className="gallery-controls">
                    <form onSubmit={handleSearch} className="search-form">
                        <input
                            type="text"
                            placeholder="Search by name, reference, or occupation..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                        {searchTerm && (
                            <button type="button" onClick={handleClearSearch} className="clear-search">
                                ✕
                            </button>
                        )}
                        <button type="submit" className="search-btn">
                            🔍 Search
                        </button>
                    </form>
                    
                    <button 
                        className={`filter-toggle ${showFilters ? 'active' : ''}`}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        ⚙️ Filters {hasActiveFilters && <span className="filter-badge">●</span>}
                    </button>
                </div>
            </div>

            {/* Filters Panel */}
            {showFilters && (
                <div className="filters-panel">
                    {/* Row 1: Basic Filters */}
                    <div className="filters-row">
                        <div className="filter-group">
                            <label>Sort By</label>
                            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                                <option value="name">Name</option>
                                <option value="age">Age</option>
                                <option value="salary">Salary</option>
                                <option value="nationality">Nationality</option>
                                <option value="experience">Experience</option>
                                <option value="occupation">Occupation</option>
                            </select>
                        </div>
                        
                        <div className="filter-group">
                            <label>Sort Direction</label>
                            <select value={sortDirection} onChange={(e) => setSortDirection(e.target.value)}>
                                <option value="desc">Descending</option>
                                <option value="asc">Ascending</option>
                            </select>
                        </div>
                        
                        <div className="filter-group">
                            <label>Nationality</label>
                            <select 
                                value={filters.nationality}
                                onChange={(e) => handleFilterChange('nationality', e.target.value)}
                            >
                                <option value="">All</option>
                                {nationalities.map(n => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="filter-group">
                            <label>Religion</label>
                            <select 
                                value={filters.religion}
                                onChange={(e) => handleFilterChange('religion', e.target.value)}
                            >
                                <option value="">All</option>
                                {religions.map(r => (
                                    <option key={r} value={r}>{r}</option>
                                ))}
                            </select>
                        </div>
                        
                        <div className="filter-group">
                            <label>Marital Status</label>
                            <select 
                                value={filters.maritalStatus}
                                onChange={(e) => handleFilterChange('maritalStatus', e.target.value)}
                            >
                                <option value="">All</option>
                                {maritalStatuses.map(m => (
                                    <option key={m} value={m}>{m}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Row 2: Range Filters */}
                    <div className="filters-row">
                        <div className="filter-group range">
                            <label>Age Range</label>
                            <div className="range-inputs">
                                <input 
                                    type="number" 
                                    placeholder="Min" 
                                    min="0"
                                    value={filters.minAge}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        // ✅ Only allow positive numbers
                                        if (val === '' || parseInt(val) >= 0) {
                                            handleFilterChange('minAge', val);
                                        }
                                    }}
                                />
                                <span>to</span>
                                <input 
                                    type="number" 
                                    placeholder="Max" 
                                    min="0"
                                    value={filters.maxAge}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === '' || parseInt(val) >= 0) {
                                            handleFilterChange('maxAge', val);
                                        }
                                    }}
                                />
                            </div>
                            {/* ✅ Show validation message */}
                            {filters.minAge && filters.maxAge && parseInt(filters.minAge) > parseInt(filters.maxAge) && (
                                <span className="filter-error">Min cannot be greater than Max</span>
                            )}
                        </div>
                        
                        <div className="filter-group range">
                            <label>Salary (SAR)</label>
                            <div className="range-inputs">
                                <input 
                                    type="number" 
                                    placeholder="Min" 
                                    min="0"
                                    value={filters.minSalary}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === '' || parseFloat(val) >= 0) {
                                            handleFilterChange('minSalary', val);
                                        }
                                    }}
                                />
                                <span>to</span>
                                <input 
                                    type="number" 
                                    placeholder="Max" 
                                    min="0"
                                    value={filters.maxSalary}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === '' || parseFloat(val) >= 0) {
                                            handleFilterChange('maxSalary', val);
                                        }
                                    }}
                                />
                            </div>
                            {filters.minSalary && filters.maxSalary && parseFloat(filters.minSalary) > parseFloat(filters.maxSalary) && (
                                <span className="filter-error">Min cannot be greater than Max</span>
                            )}
                        </div>
                        
                        <div className="filter-group range">
                            <label>Experience (Years)</label>
                            <div className="range-inputs">
                                <input 
                                    type="number" 
                                    placeholder="Min" 
                                    min="0"
                                    value={filters.minExperience}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === '' || parseInt(val) >= 0) {
                                            handleFilterChange('minExperience', val);
                                        }
                                    }}
                                />
                                <span>to</span>
                                <input 
                                    type="number" 
                                    placeholder="Max" 
                                    min="0"
                                    value={filters.maxExperience}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === '' || parseInt(val) >= 0) {
                                            handleFilterChange('maxExperience', val);
                                        }
                                    }}
                                />
                            </div>
                            {filters.minExperience && filters.maxExperience && parseInt(filters.minExperience) > parseInt(filters.maxExperience) && (
                                <span className="filter-error">Min cannot be greater than Max</span>
                            )}
                        </div>
                    </div>

                    {/* Row 3: Skills & Boolean Filters */}
                    <div className="filters-row">
                        <div className="filter-group skills">
                            <label>Skills</label>
                            <div className="skills-checkboxes">
                                {skillOptions.map(skill => (
                                    <label key={skill} className="skill-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={filters.skills.includes(skill)}
                                            onChange={() => handleSkillToggle(skill)}
                                        />
                                        {skill}
                                    </label>
                                ))}
                            </div>
                        </div>
                        
                        <div className="filter-group boolean-filters">
                            <label>Media</label>
                            <div className="boolean-options">
                                <button 
                                    className={`boolean-btn ${filters.hasVideo === true ? 'active' : ''}`}
                                    onClick={() => handleBooleanFilter('hasVideo', true)}
                                >
                                    🎥 Has Video
                                </button>
                                <button 
                                    className={`boolean-btn ${filters.hasPhoto === true ? 'active' : ''}`}
                                    onClick={() => handleBooleanFilter('hasPhoto', true)}
                                >
                                    📷 Has Photo
                                </button>
                            </div>
                        </div>
                        
                        <div className="filter-group actions">
                            <button className="clear-filters-btn" onClick={clearFilters}>
                                Clear All Filters
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Results Info */}
            {items.length > 0 && (
                <div className="results-info">
                    <span>
                        Showing {displayedCount.toLocaleString()} of {totalAvailable.toLocaleString()} applicants
                        {debouncedSearchTerm && ` matching "${debouncedSearchTerm}"`}
                        {hasActiveFilters && ' (filtered)'}
                    </span>
                </div>
            )}

            {/* Masonry Grid */}
            {items.length === 0 && loading ? (
                <div className="loader-container">
                    <div className="loader"></div>
                    <p>Loading applicants...</p>
                </div>
            ) : items.length === 0 ? (
                <div className="empty-state">
                    <p>No applicants found</p>
                    {(debouncedSearchTerm || hasActiveFilters) && (
                        <p style={{ fontSize: '14px', color: '#999', marginTop: '8px' }}>
                            Try adjusting your search terms or filters
                        </p>
                    )}
                </div>
            ) : (
                <div className="masonry-grid">
                    {getColumns().map((column, colIndex) => (
                        <div key={colIndex} className="masonry-column">
                            {column.map((applicant, index) => {
                                const isLastItem = colIndex === columnCount - 1 && 
                                                  index === column.length - 1;
                                return (
                                    <div 
                                        key={applicant.id} 
                                        className="masonry-item"
                                        ref={isLastItem ? lastItemRef : null}
                                    >
                                        <ApplicantCard
                                            applicant={applicant}
                                            onVideoClick={handleVideoClick}
                                            isVideoLoading={isVideoLoading && selectedVideo === applicant.videoUrl}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            )}

            {/* Loading More Indicator */}
            {loading && items.length > 0 && (
                <div className="loader-container">
                    <div className="loader"></div>
                    <p>Loading more applicants...</p>
                </div>
            )}

            {/* End Message */}
            {!hasMore && items.length > 0 && !loading && (
                <div className="end-message">
                    <p>✨ You've seen all {totalAvailable.toLocaleString()} applicants!</p>
                </div>
            )}
            
            {/* Debug Info */}
            {/*process.env.NODE_ENV === 'development' && (
                <div style={{ 
                    position: 'fixed', 
                    bottom: '10px', 
                    right: '10px', 
                    background: 'rgba(0,0,0,0.85)', 
                    color: '#0f0',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    zIndex: '9999',
                    fontFamily: 'monospace',
                    maxWidth: '280px',
                    lineHeight: '1.6'
                }}>
                    <div>📊 Items: {items.length}</div>
                    <div>📊 Total: {totalCount}</div>
                    <div>📊 HasMore: {hasMore ? '✅ YES' : '❌ NO'}</div>
                    <div>📊 Loading: {loading ? '⏳' : '✅'}</div>
                    <div>📊 RowNum: {lastRowNumber !== null ? lastRowNumber : 'null'}</div>
                    <div>📊 Fetching: {isFetchingRef.current ? '⏳' : '✅'}</div>
                    <div>📊 Filters: {hasActiveFilters ? '🔴' : '⚪'}</div>
                </div>
            )*/}

            {/* Video Modal */}
            {selectedVideo && (
                <div className="video-modal" onClick={handleCloseVideo}>
                    <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="video-modal-close" onClick={handleCloseVideo}>
                            ✕
                        </button>
                        <video
                            controls
                            autoPlay
                            className="video-player"
                            src={selectedVideo}
                            onError={() => {
                                alert('Failed to load video');
                                handleCloseVideo();
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default ApplicantGallery;